import type { Theme } from '@mui/material'
import type { ApexOptions } from 'apexcharts'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import appColors from 'themes/colors'
import { getDarkModeProgressColors, getLightModeProgressColors } from 'themes/theme/theme-purcent'
import { ThemeMode } from 'types/config'

interface VariableCompensationData {
  label: string
  earned: number
  remaining: number
  overage: number
  total: number
}

interface VariableCompensationChartProps {
  data: VariableCompensationData[]
  theme: Theme
  currency?: string
  currencyDecimals?: number
  height?: number
}

const VariableCompensationChart: FC<VariableCompensationChartProps> = ({
  data = [],
  theme,
  currency = 'USD',
  currencyDecimals = 2,
  height = 380,
}) => {
  const [isReady, setIsReady] = useState(false)

  // Small delay to ensure DOM is ready before rendering the chart
  useEffect(() => {
    setIsReady(false)
    const timer = setTimeout(() => setIsReady(true), 100)
    return () => clearTimeout(timer)
  }, [data, theme.palette.mode])

  // Generate colors for earned, remaining, and overage segments
  const generateColors = () => {
    const colors: string[] = []

    // Different color orders for light and dark modes
    const baseColors =
      theme.palette.mode === ThemeMode.DARK
        ? [
            appColors.turquoise, // #1DD3B0 - Primary
            appColors.caribbeanBlue, // #087575 - Primary
            appColors.charcoalBlue, // #193C4D - Accent
            appColors.pear, // #D1E231 - Accent
            appColors.kellyGreen, // #32AE2C - Accent
            appColors.crayolaRed, // #F00553 - Accent
            '#6B4E9C', // Purple - Extrapolated
            '#E67E22', // Orange - Extrapolated
            '#3498DB', // Sky Blue - Extrapolated
            '#8E44AD', // Deep Purple - Extrapolated
            '#F39C12', // Golden Yellow - Extrapolated
            '#2ECC71', // Emerald Green - Extrapolated
          ]
        : [
            appColors.turquoise, // #1DD3B0 - Primary
            appColors.caribbeanBlue, // #087575 - Primary
            '#3498DB', // Sky Blue - Extrapolated (moved to 3rd)
            '#2ECC71', // Emerald Green - Extrapolated (moved to 4th)
            appColors.pear, // #D1E231 - Accent (5th)
            appColors.charcoalBlue, // #193C4D - Accent (6th)
            appColors.kellyGreen, // #32AE2C - Accent
            appColors.crayolaRed, // #F00553 - Accent
            '#6B4E9C', // Purple - Extrapolated
            '#E67E22', // Orange - Extrapolated
            '#8E44AD', // Deep Purple - Extrapolated
            '#F39C12', // Golden Yellow - Extrapolated
          ]

    data
      .filter((item) => item && typeof item === 'object' && item.label) // Filter out null/invalid items
      .forEach((item, index) => {
        const baseColor = baseColors[index % baseColors.length]
        const progressColors =
          theme.palette.mode === ThemeMode.LIGHT
            ? getLightModeProgressColors(baseColor)
            : getDarkModeProgressColors(baseColor)

        // Earned/Progress amount
        if (item.earned > 0) {
          colors.push(progressColors.progress)
        }

        // Remaining amount
        if (item.remaining > 0) {
          colors.push(progressColors.remaining)
        }

        // Overage/Excess amount
        if (item.overage > 0) {
          colors.push(progressColors.excess)
        }
      })

    return colors
  }

  const totalData = data.reduce((sum, item) => sum + item.total, 0)

  // Calculate responsive font sizes based on height
  const baseFontSize = Math.max(10, Math.min(16, height / 16))
  const totalFontSize = Math.max(12, Math.min(18, height / 14))
  const legendFontSize = Math.max(10, Math.min(14, height / 16))

  const chartOptions: ApexOptions = {
    chart: {
      type: 'donut',
      height: height,
      animations: {
        enabled: true,
      },
    },
    colors: generateColors(),
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              color: theme.palette.text.primary,
              fontSize: `${totalFontSize}px`,
              fontWeight: 'bold',
              fontFamily: theme.typography.fontFamily,
              showAlways: true,
              show: true,
              label: 'On-Target',
              formatter(w) {
                return formatCurrency(totalData, currency, currencyDecimals)
              },
            },
            value: {
              color: theme.palette.text.primary,
              fontSize: `${baseFontSize}px`,
              fontFamily: theme.typography.fontFamily,
              show: true,
              offsetY: -5,
            },
            name: {
              color: theme.palette.text.primary,
              fontSize: `${baseFontSize}px`,
              fontFamily: theme.typography.fontFamily,
              show: true,
            },
          },
        },
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'left',
      fontSize: `${legendFontSize}px`,
      fontFamily: theme.typography.fontFamily,
      itemMargin: {
        horizontal: 4,
        vertical: 2,
      },
      markers: {
        size: 4,
        offsetX: -2,
      },
      labels: {
        colors: theme.palette.text.primary,
        useSeriesColors: false,
      },
      formatter(val, opts) {
        const seriesValue = opts?.w?.globals?.series?.[opts.seriesIndex]
        return `${val} - ${formatCurrency(seriesValue, currency, currencyDecimals)}`
      },
    },
    tooltip: {
      enabled: true,
      fixed: {
        enabled: true,
        position: 'topRight',
        offsetX: 0,
        offsetY: 0,
      },
      y: {
        formatter(val) {
          return formatCurrency(val, currency, currencyDecimals)
        },
      },
      style: {
        fontSize: '12px',
      },
      theme: theme.palette.mode === ThemeMode.DARK ? 'dark' : 'light',
    },
    labels: data
      .filter((item) => item && typeof item === 'object' && item.label) // Filter out null/invalid items
      .flatMap((item) => {
        const labels = []
        if (item.earned > 0) labels.push(`${item.label} Progress`)
        if (item.remaining > 0) labels.push(`${item.label} Remaining`)
        if (item.overage > 0) labels.push(`${item.label} Over Achievement`)
        return labels
      })
      .filter((label) => label && typeof label === 'string'), // Filter out invalid labels
  }

  const formatCurrency = (value: number, cur: string, dec: number) => {
    if (typeof value !== 'number' || isNaN(value)) return `${cur} 0`

    try {
      return new Intl.NumberFormat('default', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      }).format(value)
    } catch (error) {
      console.error('Error formatting currency:', error)
      return `${cur} ${value.toFixed(dec)}`
    }
  }

  const series = data
    .filter((item) => item && typeof item === 'object' && item.label) // Filter out null/invalid items
    .flatMap((item) => {
      const segments = []
      if (item.earned > 0) segments.push(item.earned)
      if (item.remaining > 0) segments.push(item.remaining)
      if (item.overage > 0) segments.push(item.overage)
      return segments
    })
    .filter((value) => typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0) // Filter out invalid values

  // Validate that all series values are valid numbers and we have data
  const isValidSeries =
    series.length > 0 && series.every((value) => typeof value === 'number' && !isNaN(value) && isFinite(value))

  // Early return if no valid data - but allow zero total for empty states
  if (
    !data ||
    !Array.isArray(data) ||
    data.length === 0 ||
    !isValidSeries ||
    (series.length === 0 && totalData === 0)
  ) {
    return <div>No data available for the chart</div>
  }

  return (
    <>
      {isReady && totalData > 0 ? (
        <ReactApexChart
          key={`apex-variable-chart-${theme.palette.mode}-${series.join('-')}`}
          options={chartOptions}
          series={series}
          type="donut"
          height={height}
          width="100%"
        />
      ) : (
        <div>Loading chart...</div>
      )}
    </>
  )
}

export default VariableCompensationChart
